import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Wrap Header in React.memo for performance
const Header: React.FC = React.memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 hidden"
      // Optimization: Hint browser to optimize transform/opacity changes
      style={{ willChange: 'transform, opacity' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Pill-shaped navigation that appears when scrolled */}
        <motion.div
          className={`flex items-center justify-between ${
            isScrolled 
              ? 'max-w-4xl mx-auto bg-white/10 backdrop-blur-2xl rounded-full px-8 py-4 border border-white/20 shadow-2xl mt-4' 
              : 'opacity-0 pointer-events-none'
          }`}
          style={{
            willChange: 'transform, opacity', // Optimization
            background: isScrolled 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'transparent',
            backdropFilter: isScrolled 
              ? 'blur(24px)' 
              : 'blur(0px)',
            border: isScrolled 
              ? '1px solid rgba(255, 255, 255, 0.15)' 
              : 'none',
            boxShadow: isScrolled 
              ? '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1)' 
              : 'none',
          }}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={isScrolled ? { 
            opacity: 1, 
            scale: 1, 
            y: 0 
          } : { 
            opacity: 0, 
            scale: 0.95, 
            y: -10 
          }}
          transition={{ 
            duration: 0.4, 
            ease: [0.25, 0.46, 0.45, 0.94],
            opacity: { duration: 0.3 },
            scale: { duration: 0.4 },
            y: { duration: 0.4 }
          }}
        >
          {/* Logo */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{ willChange: 'transform, opacity' }} // Optimization
          >
            <div className="bg-black text-green-400 px-4 py-2 rounded-full font-bold text-lg">
              Zeoqo.
            </div>
          </motion.div>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center space-x-8">
            {['About us', 'Services', 'Case studies', 'Blog'].map((item, index) => (
              <motion.a
                key={item}
                href={`#${item.toLowerCase().replace(' ', '-')}`}
                className="font-medium text-gray-800 hover:text-gray-600 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 10 }}
                animate={isScrolled ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ 
                  duration: 0.2,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                style={{ willChange: 'transform, opacity' }} // Optimization
              >
                {item}
              </motion.a>
            ))}
          </nav>

          {/* CTA Button */}
          <motion.button
            className="px-6 py-2 rounded-full font-medium bg-black text-white hover:bg-gray-800 transition-all duration-200 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isScrolled ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ 
              duration: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            style={{ willChange: 'transform, opacity' }} // Optimization
          >
            Get in touch
          </motion.button>
        </motion.div>

        {/* Original navigation (visible when not scrolled) */}
        <motion.div
          className={`flex items-center justify-between ${
            isScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            willChange: 'transform, opacity', // Optimization
            padding: '1.5rem 0',
          }}
          initial={{ opacity: 1 }}
          animate={{ opacity: isScrolled ? 0 : 1 }}
          transition={{ 
            duration: 0.3,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {/* Logo */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{ willChange: 'transform, opacity' }} // Optimization
          >
            <div className="bg-black text-green-400 px-4 py-2 rounded-full font-bold text-lg">
              Zeoqo.
            </div>
          </motion.div>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center space-x-8">
            {['About us', 'Services', 'Case studies', 'Blog'].map((item, index) => (
              <motion.a
                key={item}
                href={`#${item.toLowerCase().replace(' ', '-')}`}
                className="font-medium text-gray-700 hover:text-gray-900 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.2,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                style={{ willChange: 'transform, opacity' }} // Optimization
              >
                {item}
              </motion.a>
            ))}
          </nav>

          {/* CTA Button */}
          <motion.button
            className="px-6 py-2 rounded-full font-medium bg-black text-white hover:bg-gray-800 transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            style={{ willChange: 'transform, opacity' }} // Optimization
          >
            Get in touch
          </motion.button>
        </motion.div>
      </div>
    </motion.header>
  );
});

export default Header; 